import React, { useEffect, useState } from 'react';
import { getCatalogueInvoiceCoverage, InvoiceCoverage } from '../../lib/masterCatalogueApi';
import { usePermission } from '../../hooks/usePermission';

/**
 * WHY LINES OF A SEARCHED INVOICE ARE NOT IN THE GRID (NEW-5; prod USC-BUG-0022,
 * 0009, 0006).
 *
 * The grid lists rows whose invoice number contains the search, and a row carries
 * only the invoice that PRICES it. An invoice nobody confirmed, a line that joined
 * a row another invoice prices, and a line switched off at Confirm & sync all look
 * alike there: missing. When the committed search is a vendor invoice's number,
 * this says which of those happened, line by line
 * (BUSINESS_RULES#catalogue-invoice-coverage). The sentences are the server's.
 *
 * Read-only. Asked only for a user who may read vendor invoices, the same gate the
 * endpoint applies.
 */
export function InvoiceCoverageNote({ search }: { search: string }) {
  const { canView } = usePermission('vendors');
  const term = search.trim();
  const [invoices, setInvoices] = useState<InvoiceCoverage[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setInvoices([]);
    setFailed(false);
    if (!canView || !term) return undefined;
    // Last search wins: a slow answer for an earlier term must not replace this one.
    let current = true;
    getCatalogueInvoiceCoverage(term).then(
      (res) => {
        if (current) setInvoices(res.data.invoices);
      },
      (err) => {
        if (!current) return;
        // QA retest, 9 Oct 2026, item 55: a 300-character search (clearly not
        // an invoice number a person typed) refused server-side
        // (`INVOICE_NUMBER_INVALID`, `catalogueInvoiceCoverage.service.js`
        // MAX_TERM_LENGTH) and that refusal was shown exactly like a real
        // failure to check — an alert quoting the whole term, unwrapped. This
        // search is simply not an invoice-number lookup; it is not an error,
        // so there is nothing to say.
        if (err?.code === 'INVOICE_NUMBER_INVALID') return;
        setFailed(true);
      }
    );
    return () => {
      current = false;
    };
  }, [canView, term]);

  if (failed) {
    return (
      <div className="px-6 pt-3">
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 break-words"
        >
          Could not check whether invoice "{term}" has lines missing from this list.
        </div>
      </div>
    );
  }
  if (!invoices.length) return null;

  return (
    <div className="px-6 pt-3" data-testid="invoice-coverage-note">
      <div
        role="status"
        className="space-y-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs leading-[18px] text-blue-900"
      >
        {invoices.map((inv) => (
          <div key={inv.id}>
            <div className="font-semibold">
              {[`Invoice ${inv.invoiceNumber || term}`, inv.supplier, inv.invoiceDate].filter(Boolean).join(' · ')}
            </div>
            <div>{inv.message}</div>
            {inv.lines.length > 0 && (
              <ul className="mt-1 list-disc pl-5">
                {inv.lines.map((line) => (
                  <li key={line.lineNo}>{line.message}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
